import { v4 } from 'uuid';
import { getSyncAccountData, loadSecretSafe } from './trello';
import { REQUEST_METHODS, MAESTRO_BASE_API } from '../consts';
import { RequestAllowedDelayExceeded, TrelloInstanceNotProvided } from './errors';
import { delay } from './helpers';
import { logger } from './logger';
import * as Fetch from './fetch';

const HTTP_NO_CONTENT = 204;
const DEFAULT_FETCH_TIMEOUT = 8000;

const buildRequestOptions = (method, options) => {
  const { body = {}, token } = options;
  const requestOptions = {
    method: method.toUpperCase(),
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'Unito-Correlation-Id': v4(),
      'Unito-GMT-offset': new Date().getTimezoneOffset() / 60,
    },
    body: method !== REQUEST_METHODS.GET ? JSON.stringify(body) : undefined,
  };

  if (token) {
    requestOptions.headers.Authorization = `Bearer ${token}`;
  }

  return requestOptions;
};

/**
 * @param {Response} response
 * @returns {Promise<any>}
 */
export const processResponse = async (response) => {
  if (response.status === HTTP_NO_CONTENT) {
    return;
  }

  // Reject all responses of content type that are not json
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.startsWith('application/json')) {
    const text = await response.text();
    return Promise.reject({
      logMessage: text,
      status: response.status,
      name: response.name,
    });
  }

  return response.json();
};

// if some requests to Maestro take too much time to complete
// some PowerUp capabilities might not load correctly
export const fetchWithTimeout = async (url, requestOptions, timeoutDelay = DEFAULT_FETCH_TIMEOUT) => {
  // Initiate the fetch and delay but don't await them yet, just keep the promise.
  const fetchPromise = fetch(url, requestOptions);

  const delayPromise = delay(timeoutDelay);

  // Wait for whichever promise completes first.
  const resp = await Promise.race([fetchPromise, delayPromise]);
  if (!resp) {
    throw new RequestAllowedDelayExceeded(timeoutDelay, url.pathname);
  }

  return resp;
};

/**
 * @param {string} url
 * @param {{ [key: string]: any }} requestOptions
 * @param {number} timeoutDelay
 * @returns {Promise<Response>}
 */
export const callFetch = async (url, requestOptions, timeoutDelay) => {
  // Promise.race used in fetchWithTimeout is not supported in IE
  const fetchFn = timeoutDelay && Promise.race ? fetchWithTimeout : fetch;
  return fetchFn(url, requestOptions, timeoutDelay);
};

// urlSearch passed as parameter to facilitate tests.
export const getQueryParameter = (parameter = '', urlSearch = window.location.search) => {
  const searchParams = new URLSearchParams(urlSearch);
  return searchParams.get(parameter);
};

export const getMaestroEmbedUrl = (params) => {
  const queryString = Object.keys(params)
    .map((name) => `${name}=${encodeURIComponent(params[name])}`)
    .join('&');
  return `${process.env.REACT_APP_MAESTRO_BASE_URL}/api/embed/trello?${queryString}`;
};

/**
 * @param {string} endpoint The maestro endpoint to hit
 * @param {boolean} retryable Whether the request can be retried. GETs are generally retryable,
 *        and some POST/PATCH/PUT/DELETEs are too, it's up to you to go look at what Maestro does!
 *        Supporting retries as much as possible is important for reliability, because browsers are
 *        weird and sometimes throw "Failed to fetch" for unclear reasons.
 * @param {any} options
 */
export const callMaestro = async (
  endpoint,
  retryable,
  { method = REQUEST_METHODS.GET, baseApi = MAESTRO_BASE_API, options = {}, t, timeoutDelay } = {},
) => {
  if (t === undefined) {
    throw new TrelloInstanceNotProvided(
      'callMaestro was not provided a Trello instance, but it is needed for it to work',
    );
  }

  const requestOptions = buildRequestOptions(method, {
    ...options,
    token: await loadSecretSafe('token', t),
  });
  const url = new URL(encodeURI(`${process.env.REACT_APP_MAESTRO_BASE_URL}/${baseApi}/${endpoint}`));
  Object.entries(options.params || {}).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  let response;
  const maxRetries = 3;
  const baseDelayMs = 100; // not too big, must remain "acceptable in interactive webapp"

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const dateBeforeFetch = Date.now();
    try {
      response = await Fetch.callFetch(url, requestOptions, timeoutDelay);
      const processResponse = await Fetch.processResponse(response);
      if (!response.ok) {
        throw processResponse;
      }
      if (attempt > 0) {
        logger.info(
          `Successfully called Maestro ${method} ${endpoint} after ` +
            `${attempt + 1} attempts following muddy client-side trouble`,
        );
      }
      return processResponse;
    } catch (e) {
      const requestDuration = Date.now() - dateBeforeFetch;
      const correlationId = response?.headers?.get('unito-correlation-id');
      const enrichedError = {
        message: e.message,
        name: e.name,
        ...e,
        correlationId,
      };
      const profileId = t.getContext().member;
      const { code, name } = enrichedError;
      const optionsString = JSON.stringify(options, null, 2);

      // Errors "Failed to fetch" & "Load failed" (on iOS) may be out-of-our-control
      // client-side trouble (browser, extensions, ad blockers).
      // - https://sentry.io/answers/failed-to-fetch-javascript/
      // - https://sentry.io/answers/load-failed-javascript/
      //
      // Warning: it's not *guaranteed* client-side trouble! Our own Unito mistakes
      // may well cause them (e.g. bad URL, CORS). But retrying them when it makes sense,
      // and lowering their logLevel to `warn` to reduce noise when operating normally.
      const mayBeMuddyClientSideTrouble =
        e.name === 'TypeError' && (e.message === 'Failed to fetch' || e.message === 'Load failed');

      const logDetails =
        `duration: ${requestDuration} ms, timeout: ${timeoutDelay} ms, ` +
        `options ${optionsString}, enrichedError: ${JSON.stringify(enrichedError)}, ` +
        `syncAccountData: ${JSON.stringify(await getSyncAccountData())}`;

      if (mayBeMuddyClientSideTrouble) {
        if (!retryable) {
          logger.error(
            `Will NOT retry calling Maestro ${method} ${endpoint} although error is likely ` +
              `muddy client-side trouble: not retryable. ${logDetails}`,
          );
        } else if (attempt >= maxRetries) {
          logger.error(
            `Will NOT retry calling Maestro ${method} ${endpoint} although error is likely ` +
              `muddy client-side trouble: already retried ${attempt + 1} times. ${logDetails}`,
          );
        } else {
          const retryDelayMs = baseDelayMs * Math.pow(2, attempt);
          logger.warn(
            `Will retry calling Maestro ${method} ${endpoint} (attempt ${attempt + 1}/${maxRetries + 1}) after ` +
              `${retryDelayMs} ms delay: error is likely muddy client-side trouble. ${logDetails}`,
          );
          await delay(retryDelayMs);
          continue;
        }
      } else {
        logger.error(`Error calling Maestro ${method} ${endpoint} . ${logDetails}`);
      }

      if (code === 401) {
        await t.clearSecret('token');
        logger.info(`Cleared secret for profileId ${profileId} with 401 error: ${name}`);
      }

      throw enrichedError;
    }
  }
};
